import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { ArrowLeft, X, Search, Plus, FolderTree } from "lucide-react";
import TeclyPullRefresh from "@/components/TeclyPullRefresh";
import { TechArticleCardCompact } from "@/components/TechArticleCardCompact";
import { BottomNavigation } from "@/components/BottomNavigation";
import { ArticleDetail } from "@/components/ArticleDetail";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { useArticles, Article } from "@/hooks/useArticles";
import { motion } from "framer-motion";
import Fuse from "fuse.js";
import { useAdminRole } from "@/hooks/useAdminRole";
import ArticleEditor from "@/components/ArticleEditor";
import CategoryManager from "@/components/CategoryManager";
import NewsFilterManager from "@/components/NewsFilterManager";
import { toast } from "sonner";

interface NewsFilterDB {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
}

interface CategoryDB {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

type SortOption = string;
type CategoryFilter = string | null; // null = all categories

const News = () => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isFilterManagerOpen, setIsFilterManagerOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [newsFilters, setNewsFilters] = useState<NewsFilterDB[]>([]);
  const [categories, setCategories] = useState<CategoryDB[]>([]);
  const [articleCategories, setArticleCategories] = useState<Record<string, string[]>>({});
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>(null);
  const navigate = useNavigate();

  const { articles, userLikes, loading, recordView, toggleLike, refetch } = useArticles(user);
  const { isAdmin } = useAdminRole(user?.id ?? null);

  // Pull-to-refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const PULL_THRESHOLD = 80;

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([refetch(), fetchCategories(), fetchArticleCategories()]);
    setIsRefreshing(false);
  }, [refetch]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, 120));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      handleRefresh();
    }
    setPullDistance(0);
    isPulling.current = false;
  }, [pullDistance, isRefreshing, handleRefresh]);

  // Setup Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(articles, {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'description', weight: 0.3 },
        { name: 'slug', weight: 0.2 },
      ],
      threshold: 0.4, // Fuzzy tolerance (0 = exact, 1 = match anything)
      ignoreLocation: true,
      includeScore: true,
    });
  }, [articles]);

  // Filter and sort articles
  const filteredAndSortedArticles = useMemo(() => {
    let result = articles;
    
    // Apply fuzzy search if query exists
    if (searchQuery.trim()) {
      const fuseResults = fuse.search(searchQuery);
      result = fuseResults.map(r => r.item);
    }
    
    // Apply category filter
    if (selectedCategory) {
      result = result.filter(article => {
        const articleCats = articleCategories[article.id] || [];
        return articleCats.includes(selectedCategory);
      });
    }
    
    // Apply sorting
    return [...result].sort((a, b) => {
      if (sortBy === 'likes') {
        return b.likes_count - a.likes_count;
      } else if (sortBy === 'views') {
        return b.views_count - a.views_count;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [articles, searchQuery, sortBy, fuse, selectedCategory, articleCategories]);

  // Fetch news filters from database
  const fetchNewsFilters = async () => {
    const { data } = await supabase
      .from("news_filters")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    
    if (data) setNewsFilters(data);
  };

  // Fetch categories from database
  const fetchCategories = async () => {
    const { data } = await supabase
      .from("categories")
      .select("id, name, slug, sort_order")
      .order("sort_order", { ascending: true });
    
    if (data) setCategories(data);
  };

  // Fetch article-category relationships
  const fetchArticleCategories = async () => {
    const { data } = await supabase
      .from("article_categories")
      .select("article_id, category_id");
    
    if (data) {
      const mapping: Record<string, string[]> = {};
      data.forEach(ac => {
        if (!mapping[ac.article_id]) {
          mapping[ac.article_id] = [];
        }
        mapping[ac.article_id].push(ac.category_id);
      });
      setArticleCategories(mapping);
    }
  };

  useEffect(() => {
    fetchNewsFilters();
    fetchCategories();
    fetchArticleCategories();
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // History-based back gesture for article detail
  useEffect(() => {
    if (selectedArticle) {
      window.history.pushState({ articleDetail: true }, '');
      const handlePopState = () => {
        setSelectedArticle(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [selectedArticle]);

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    recordView(article.id);
  };

  const handleLike = (articleId: string) => {
    toggleLike(articleId);
  };

  const handleEditArticle = () => {
    if (selectedArticle) {
      setEditingArticle(selectedArticle);
      setSelectedArticle(null);
      setIsEditorOpen(true);
    }
  };

  const handleDeleteArticle = async () => {
    if (!selectedArticle) return;

    try {
      // Delete article categories first
      await supabase
        .from("article_categories")
        .delete()
        .eq("article_id", selectedArticle.id);

      // Delete article
      const { error } = await supabase
        .from("articles")
        .delete()
        .eq("id", selectedArticle.id);

      if (error) throw error;

      toast.success("Articolo eliminato!");
      setSelectedArticle(null);
      refetch();
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Errore nell'eliminazione dell'articolo");
    }
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingArticle(null);
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div
        ref={scrollRef}
        className="min-h-screen bg-background pb-32 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to refresh indicator */}
        <TeclyPullRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate("/")}
            aria-label="Indietro"
            className="lg-icon-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-foreground flex-1">Tutte le News</h1>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={() => setIsCategoryManagerOpen(true)}
                className="p-2 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-all duration-300 hover:scale-110 active:scale-90"
                title="Gestione Categorie"
              >
                <FolderTree className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsEditorOpen(true)}
                className="p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-110 active:scale-90"
                title="Nuovo Articolo"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Search Bar */}
      <div className="container mx-auto px-4 pt-3">
        <motion.div 
          className={`relative transition-all duration-300 ${isSearchFocused ? 'ring-2 ring-primary/50' : ''} rounded-xl`}
          initial={false}
          animate={{ scale: isSearchFocused ? 1.01 : 1 }}
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Cerca articoli..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            className="w-full pl-10 pr-4 py-2.5 bg-muted/50 rounded-xl border-none outline-none text-foreground placeholder:text-muted-foreground text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </motion.div>
      </div>

      {/* Category Filter Tabs with Blue Animation */}
      {categories.length > 0 && (
        <div className="container mx-auto px-4 pt-3">
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex-shrink-0 ${
                selectedCategory === null
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Tutte
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex-shrink-0 ${
                  selectedCategory === category.id
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Articles List */}
      <main className="container mx-auto px-4 py-2">
        {filteredAndSortedArticles.length === 0 && searchQuery ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nessun articolo trovato per "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-4 text-primary hover:underline text-sm"
            >
              Cancella ricerca
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredAndSortedArticles.map((article) => (
              <TechArticleCardCompact
                key={article.id}
                image={article.image}
                title={article.title}
                description={article.description}
                views={article.views_count}
                liked={userLikes.has(article.id)}
                likes={article.likes_count}
                onLike={() => handleLike(article.id)}
                onClick={() => handleArticleClick(article)}
                articleId={article.id}
              />
            ))}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      {user && <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} activeTab="news" />}

      {/* Article Detail View */}
      {selectedArticle && (
        <ArticleDetail 
          image={selectedArticle.image}
          title={selectedArticle.title}
          description={selectedArticle.description}
          views={selectedArticle.views_count}
          liked={userLikes.has(selectedArticle.id)}
          content={selectedArticle.content as { type: "text" | "image"; content: string }[] | undefined}
          isAdmin={isAdmin}
          articleId={selectedArticle.id}
          onLike={() => handleLike(selectedArticle.id)}
          onClose={() => window.history.back()}
          onEdit={handleEditArticle}
          onDelete={handleDeleteArticle}
        />
      )}

      {/* Menu Drawer */}
      {user && <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />}

      {/* Article Editor for Admin */}
      {isAdmin && (
        <>
          <ArticleEditor
            isOpen={isEditorOpen}
            onClose={handleEditorClose}
            onArticleCreated={refetch}
            editArticle={editingArticle}
          />
          <CategoryManager
            isOpen={isCategoryManagerOpen}
            onClose={() => setIsCategoryManagerOpen(false)}
          />
          <NewsFilterManager
            isOpen={isFilterManagerOpen}
            onClose={() => {
              setIsFilterManagerOpen(false);
              fetchNewsFilters(); // Refresh filters after changes
            }}
          />
        </>
      )}
      </div>
    </PageTransition>
  );
};

export default News;
